import type {
  ColorField,
  GradientField,
  RadiusField,
  ShadowField,
  SizeField,
  SpacingField,
} from '../types/styles'
import { SHADOW_NONE_KEY } from '../types/styles'
import { usePipeIfAvailable } from './usePipe'
import { isRtlLanguage } from '../lib/isRtlLanguage'
import { isMaxWidthAlign, maxWidthMarginStyle, type MaxWidthAlign } from '../lib/maxWidthAlign'

/**
 * Resolve the active text direction for spacing swaps, reading the pipe context
 * directly so call sites don't have to thread `isRtl` through every call.
 *
 * `usePipeIfAvailable()` is guarded by `hasInjectionContext()`, so this is a
 * safe no-op → `false` (LTR) outside a component/render context. The spacing
 * computeds that call `getSpacingStyleData` evaluate during render (injection
 * context present) and, by reading `const.system.language` here, gain a reactive
 * dependency on it — so an editor language switch re-flips spacing correctly.
 * Derives from the language code (matching `useDirection`); we avoid
 * `const.system.isRtl` because the editor seeds it `false`, which would mask the
 * language. Pass an explicit `isRtl` to `getSpacingStyleData` to override.
 */
function resolveDirectionIsRtl(): boolean {
  const pipe = usePipeIfAvailable()
  if (!pipe) return false
  return isRtlLanguage(pipe.getData('const.system.language') as string | undefined)
}

export function getColorStyleData(
  style?: ColorField,
  defaultValue: string | undefined = undefined,
): string | undefined {
  if (!style) {
    return defaultValue
  }
  if (style.type === 'color_raw') {
    return style.data.data
  } else if (style.type === 'color_style') {
    return `var(--${style.key})`
  } else if (style.type === 'color_constant') {
    // TODO: resolve constant value
    return defaultValue
  }
  return defaultValue
}

export function getSpacingStyleData(
  style?: SpacingField,
  defaultValue: string | undefined = undefined,
  isRtl?: boolean,
): string | undefined {
  if (!style) {
    return defaultValue
  }
  // A shape whose sides are all empty is the author picking a layout and leaving
  // the values alone — it means the same as storing nothing, so the caller's
  // default is returned whole rather than spliced into one slot of a shorthand.
  // A side left empty next to a filled one falls back to `0`, never `undefined`.
  const side = (value?: SizeField) => getSizeStyleData(value, defaultValue) ?? '0'
  if (style.type === 'all') {
    return getSizeStyleData(style.all, defaultValue)
  } else if (style.type === 'two_sides') {
    if (!style.horizontal && !style.vertical) return defaultValue
    const horizontal = side(style.horizontal)
    const vertical = side(style.vertical)
    // `horizontal` applies to both sides symmetrically — RTL-agnostic.
    return `${vertical} ${horizontal}`
  } else if (style.type === 'separate_sides') {
    if (!style.top && !style.right && !style.bottom && !style.left) return defaultValue
    const top = side(style.top)
    const right = side(style.right)
    const bottom = side(style.bottom)
    const left = side(style.left)
    // The CSS `margin`/`padding` shorthand is physical (top right bottom left)
    // and is NOT auto-flipped by `dir`. Under RTL swap the horizontal sides so
    // an authored "left"/"right" maps to the visual side the author expects.
    // Direction is resolved from the pipe context by default (see
    // resolveDirectionIsRtl); pass `isRtl` explicitly to override.
    const rtl = isRtl ?? resolveDirectionIsRtl()
    return rtl ? `${top} ${left} ${bottom} ${right}` : `${top} ${right} ${bottom} ${left}`
  }
  return defaultValue
}

/**
 * The margin style of a container that can take a max width, placed by `maxWidthAlign`
 * (see render/lib/maxWidthAlign.ts): the authored `margin`, and — with a max width — the
 * placed sides that align the box once that max width caps it. `fallback` is what an
 * unset alignment means for the component — `undefined` keeps the authored margin alone,
 * as a container whose max width predates the alignment field always rendered. Spread it
 * into the style object, and give the element MAX_WIDTH_ALIGNED_CLASS when
 * `isMaxWidthAligned` says so — the class is what applies the placed sides.
 */
export function getMaxWidthMarginStyleData(
  margin: SpacingField | undefined,
  maxWidth: string | undefined,
  align: string | undefined,
  fallback: MaxWidthAlign | undefined,
): Record<string, string> {
  const rtl = resolveDirectionIsRtl()
  const side = (value?: SizeField) => getSizeStyleData(value) ?? '0px'
  let left = '0px'
  let right = '0px'
  if (margin?.type === 'all') {
    left = right = side(margin.all)
  } else if (margin?.type === 'two_sides') {
    left = right = side(margin.horizontal)
  } else if (margin?.type === 'separate_sides') {
    // Physical, like the shorthand: getSpacingStyleData swaps an authored left / right
    // under RTL.
    left = side(rtl ? margin.right : margin.left)
    right = side(rtl ? margin.left : margin.right)
  }
  return maxWidthMarginStyle(
    getSpacingStyleData(margin, undefined, rtl),
    { left, right },
    maxWidth,
    isMaxWidthAlign(align) ? align : fallback,
    rtl,
  )
}

/**
 * @param opacity a fill-opacity field value, 0–1
 * @returns the percentage `fadeColor` takes, or `undefined` when unset or fully opaque
 */
export function getOpacityPercent(opacity?: number): string | undefined {
  if (opacity == null || opacity >= 1) return undefined
  return `${Math.round(Math.max(0, opacity) * 100)}%`
}

/**
 * @param color a resolved CSS colour — a literal or a `var(--token)`
 * @param percent from {@link getOpacityPercent}
 * @returns `color` mixed with `transparent` down to `percent`, or `color` unchanged without one
 */
export function fadeColor(color: string | undefined, percent?: string): string | undefined {
  if (!color || !percent) return color
  return `color-mix(in srgb, ${color} ${percent}, transparent)`
}

export function getGradientStyleData(
  gradient?: GradientField,
  defaultValue: string | undefined = undefined,
  opacityPercent?: string,
): string | undefined {
  if (!gradient) return defaultValue
  if (gradient.type === 'linear_gradient') {
    if (!gradient.stops || gradient.stops.length < 2) return defaultValue
    const sorted = [...gradient.stops].sort((a, b) => a.position - b.position)
    const parts = sorted.map((stop) => {
      const color = fadeColor(getColorStyleData(stop.color) ?? 'transparent', opacityPercent)
      return `${color} ${stop.position}%`
    })
    return `linear-gradient(${gradient.angle}deg, ${parts.join(', ')})`
  }
  return defaultValue
}

export function getShadowStyleData(
  style?: ShadowField,
  defaultValue: string | undefined = undefined,
): string | undefined {
  if (!style) {
    return defaultValue
  }
  if (style.type === 'shadow_style') {
    // "None" key or empty key means no shadow. Return undefined so composeShadow
    // skips this side, instead of an invalid var(--) that would break the whole
    // box-shadow.
    if (!style.key || style.key === SHADOW_NONE_KEY) {
      return undefined
    }
    return `var(--${style.key})`
  }
  return defaultValue
}

export function composeShadow(
  drop?: ShadowField,
  inner?: ShadowField,
  dropFallback?: string,
  innerFallback?: string,
): string {
  const parts = [
    getShadowStyleData(drop, dropFallback),
    getShadowStyleData(inner, innerFallback),
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : 'none'
}

/**
 * `border-radius` for a {@link RadiusField}. A bare `SizeField` (the legacy
 * shape, and the picker's "All corners" mode) resolves exactly as before.
 *
 * The `separate_corners` arm emits the 4-value shorthand, whose order is
 * *physical* (top-left → top-right → bottom-right → bottom-left) and is not
 * flipped by `dir`, so — mirroring `getSpacingStyleData` — the horizontal pairs
 * are swapped under RTL.
 */
export function getRadiusStyleData(
  style?: RadiusField,
  defaultValue: string | undefined = undefined,
  isRtl?: boolean,
): string | undefined {
  if (!style) {
    return defaultValue
  }
  if (style.type === 'separate_corners') {
    if (!style.topLeft && !style.topRight && !style.bottomRight && !style.bottomLeft) {
      return defaultValue
    }
    const topLeft = getSizeStyleData(style.topLeft, '0')
    const topRight = getSizeStyleData(style.topRight, '0')
    const bottomRight = getSizeStyleData(style.bottomRight, '0')
    const bottomLeft = getSizeStyleData(style.bottomLeft, '0')
    const rtl = isRtl ?? resolveDirectionIsRtl()
    return rtl
      ? `${topRight} ${topLeft} ${bottomLeft} ${bottomRight}`
      : `${topLeft} ${topRight} ${bottomRight} ${bottomLeft}`
  }
  return getSizeStyleData(style, defaultValue)
}

export function getSizeStyleData(
  style?: SizeField,
  defaultValue: string | undefined = undefined,
): string | undefined {
  if (!style) {
    return defaultValue
  }
  if (style.type === 'size_raw') {
    return `${style.data.data}${style.data.unit}`
  } else if (style.type === 'size_style') {
    return `var(--${style.key})`
  } else if (style.type === 'size_constant') {
    // TODO: resolve constant value
    return defaultValue
  }
  return defaultValue
}
